import type { ChangeEvent, Dispatch, ReactNode, SetStateAction } from 'react';

import type { PrioritizedWorker } from '../../../../components/nors/WorkerListWithPriority';
import { isRetentionPoliciesBE } from 'utils/retentionPolicies';
import { readBrandingImageValue } from 'helpers/brandingImage';
import { DEFAULT_FISCAL_PERIOD_CONFIG, isFiscalPeriodConfig, normalizeFiscalPeriodConfig } from 'utils/fiscalPeriods';

import BrandingFields from './BrandingFields';
import DefaultTimezoneFields from './DefaultTimezoneFields';
import FiscalPeriodsFields from './FiscalPeriodsFields';
import GeneralFields from './GeneralFields';
import RetentionPoliciesFields from './RetentionPoliciesFields';
import SMTPFields from './SMTPFields';

/**
 * Shared props for rendering a config-type field set inside EditConfigItem.
 */
export interface ConfigFieldsContext {
  readonly selectedKey: string;
  readonly setSelectedKey: (key: string) => void;
  readonly formData: unknown;
  /** formData shapes differ per key; tightening belongs with step 3 PR D. */
  readonly setFormData: Dispatch<SetStateAction<any>>;
  readonly setJsonValid: (valid: boolean) => void;
  readonly jsonValid: boolean;
  readonly handleInputChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  readonly selectedWorkers: PrioritizedWorker[];
  readonly setSelectedWorkers: (workers: PrioritizedWorker[]) => void;
  readonly onTimezoneChange: (value: string) => void;
  readonly timezoneValidationMessage: string;
}

export interface ConfigTypeActivateHelpers {
  readonly formData: unknown;
  readonly setJsonValid: (valid: boolean) => void;
  readonly setTimezoneValidationMessage: (message: string) => void;
  readonly applyDefaultTimezoneValidation: (timezone: string) => boolean;
}

export interface ConfigTypeSaveHelpers {
  readonly applyDefaultTimezoneValidation: (timezone: string) => boolean;
}

/**
 * Per-key behaviour for the configuration edit modal: default seed on type
 * select, mount-time coercion, and which fields component to render.
 * Mirrors the action-registry idea — one lookup instead of parallel if-chains.
 */
export interface ConfigTypeDefinition {
  /** Option label in the Config Type dropdown (create flow). */
  readonly optionLabel: string;
  /**
   * When set, choosing this type in the create dropdown seeds formData.
   * Omitted types (smtp / general) leave formData unchanged on select.
   */
  readonly seedOnSelect?: unknown;
  /**
   * Coerce formData when this key becomes active. Return the next value, or
   * `undefined` to leave formData alone.
   */
  readonly coerceFormData?: (formData: unknown) => unknown | undefined;
  /** Side effects after the key is selected (validation, clearing messages). */
  readonly onActivate?: (helpers: ConfigTypeActivateHelpers) => void;
  /** Shape formData into the value persisted on save (defaults to formData as-is). */
  readonly prepareSave?: (formData: unknown) => unknown;
  /** Return false to abort save after {@link prepareSave}. */
  readonly validateBeforeSave?: (value: unknown, helpers: ConfigTypeSaveHelpers) => boolean;
  readonly renderFields: (ctx: ConfigFieldsContext) => ReactNode;
}

const clearValidationOnActivate: ConfigTypeDefinition['onActivate'] = ({ setJsonValid, setTimezoneValidationMessage }) => {
  setTimezoneValidationMessage('');
  setJsonValid(true);
};

const extractTimezone = (formData: unknown): string => {
  if (typeof formData === 'string') return formData || 'UTC';
  if (typeof formData === 'object' && formData !== null && typeof (formData as { timezone?: unknown }).timezone === 'string') {
    return (formData as { timezone: string }).timezone || 'UTC';
  }
  return 'UTC';
};

const brandingType: ConfigTypeDefinition = {
  optionLabel: 'Branding',
  seedOnSelect: { image: null },
  coerceFormData: (formData) => ({ image: readBrandingImageValue(formData) }),
  onActivate: clearValidationOnActivate,
  // Narrow to just the image: the editor writes into a shared formData bag, so
  // saving it verbatim would carry over stray keys from a previously selected
  // config type.
  prepareSave: (formData) => ({ image: readBrandingImageValue(formData) }),
  renderFields: (ctx) => <BrandingFields formData={ctx.formData} setFormData={ctx.setFormData} setJsonValid={ctx.setJsonValid} />,
};

const retentionType: ConfigTypeDefinition = {
  optionLabel: 'Retention Policies',
  seedOnSelect: {},
  coerceFormData: (formData) => (isRetentionPoliciesBE(formData) ? undefined : {}),
  onActivate: clearValidationOnActivate,
  renderFields: (ctx) => (
    <RetentionPoliciesFields
      selectedKey={ctx.selectedKey}
      setSelectedKey={ctx.setSelectedKey}
      formData={ctx.formData}
      setFormData={ctx.setFormData}
      setJsonValid={ctx.setJsonValid}
    />
  ),
};

const fiscalPeriodsType: ConfigTypeDefinition = {
  optionLabel: 'Fiscal Periods',
  seedOnSelect: DEFAULT_FISCAL_PERIOD_CONFIG,
  coerceFormData: (formData) => (isFiscalPeriodConfig(formData) ? undefined : normalizeFiscalPeriodConfig(formData)),
  onActivate: clearValidationOnActivate,
  renderFields: (ctx) => (
    <FiscalPeriodsFields
      selectedKey={ctx.selectedKey}
      setSelectedKey={ctx.setSelectedKey}
      formData={ctx.formData}
      setFormData={ctx.setFormData}
      setJsonValid={ctx.setJsonValid}
    />
  ),
};

const defaultTimezoneType: ConfigTypeDefinition = {
  optionLabel: 'Default Timezone',
  seedOnSelect: { timezone: 'UTC' },
  coerceFormData: (formData) => ({ timezone: extractTimezone(formData) }),
  onActivate: ({ formData, applyDefaultTimezoneValidation }) => {
    applyDefaultTimezoneValidation(extractTimezone(formData));
  },
  prepareSave: (formData) => {
    const timezone = extractTimezone(formData).trim();
    return { timezone: timezone.length > 0 ? timezone : 'UTC' };
  },
  validateBeforeSave: (value, { applyDefaultTimezoneValidation }) => {
    const timezone =
      typeof value === 'object' && value !== null && typeof (value as { timezone?: unknown }).timezone === 'string'
        ? (value as { timezone: string }).timezone
        : 'UTC';
    return applyDefaultTimezoneValidation(timezone);
  },
  renderFields: (ctx) => (
    <DefaultTimezoneFields
      selectedKey={ctx.selectedKey}
      setSelectedKey={ctx.setSelectedKey}
      formData={ctx.formData}
      onTimezoneChange={ctx.onTimezoneChange}
      jsonValid={ctx.jsonValid}
      validationMessage={ctx.timezoneValidationMessage}
    />
  ),
};

const smtpType: ConfigTypeDefinition = {
  optionLabel: 'SMTP',
  onActivate: clearValidationOnActivate,
  renderFields: (ctx) => (
    <SMTPFields
      formData={ctx.formData}
      onInputChange={ctx.handleInputChange}
      selectedWorkers={ctx.selectedWorkers}
      setSelectedWorkers={ctx.setSelectedWorkers}
    />
  ),
};

/** Fallback for free-form JSON configs (and the "general" create option). */
export const generalConfigType: ConfigTypeDefinition = {
  optionLabel: 'general',
  onActivate: clearValidationOnActivate,
  renderFields: (ctx) => (
    <GeneralFields
      selectedKey={ctx.selectedKey}
      setSelectedKey={ctx.setSelectedKey}
      formData={ctx.formData}
      setFormData={ctx.setFormData}
      jsonValid={ctx.jsonValid}
      setJsonValid={ctx.setJsonValid}
    />
  ),
};

/** Named config keys with dedicated editors. Unknown keys use {@link generalConfigType}. */
export const CONFIG_TYPE_BY_KEY: Readonly<Record<string, ConfigTypeDefinition>> = {
  branding: brandingType,
  smtp: smtpType,
  default_timezone: defaultTimezoneType,
  retention_policies: retentionType,
  fiscal_periods: fiscalPeriodsType,
};

/** Create-flow Config Type `<option>` list (value + label). */
export const CONFIG_TYPE_OPTIONS: readonly { readonly value: string; readonly label: string }[] = [
  { value: 'smtp', label: smtpType.optionLabel },
  { value: 'default_timezone', label: defaultTimezoneType.optionLabel },
  { value: 'retention_policies', label: retentionType.optionLabel },
  { value: 'fiscal_periods', label: fiscalPeriodsType.optionLabel },
  { value: 'branding', label: brandingType.optionLabel },
  { value: 'general', label: 'general' },
];

/**
 * Look up a dedicated editor for `key`. Must not use bare property access:
 * `CONFIG_TYPE_BY_KEY` inherits from Object.prototype, so keys like
 * `__proto__` / `constructor` / `toString` would resolve to inherited
 * members (truthy, no `renderFields`) and crash EditConfigItem at render.
 */
export const resolveConfigType = (key: string): ConfigTypeDefinition => {
  if (!Object.prototype.hasOwnProperty.call(CONFIG_TYPE_BY_KEY, key)) {
    return generalConfigType;
  }
  const dedicated = CONFIG_TYPE_BY_KEY[key];
  return dedicated ?? generalConfigType;
};
